import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import { MenuItem } from 'src/app/shared/framework/navigation/models/MenuItem';
import { GeneralActor } from 'src/app/shared/general-enums/GeneralActor';
import { GeneralStatus } from 'src/app/shared/general-enums/GeneralStatus';
import { Picklist } from '../../user-defined-fields/models/Picklist';
import { CustomFieldValueStatus } from '../enums/CustomFieldValueStatus';
import { FieldType } from '../enums/FieldType';
import { FieldValidationType } from '../enums/FieldValidationType';
import { CompositeValue, CustomField, CustomFieldValue } from '../models/CustomField';
import { CustomFieldCategory } from '../models/CustomFieldCategory';
import {
  CompositeCellVm,
  CompositeFieldChangeDto,
  CompositeFieldLogDto,
  CompositeFieldVm,
  CompositeRowChangeDto,
  CompositeRowVm,
  CustomFieldCategoryVm,
  CustomFieldChangeOperation,
  CustomFieldContentDto, CustomFieldTabContentDto,
  CustomFieldTabContentVm,
  CustomFieldVm,
  FieldLogDto,
  SaveCustomFieldContentCommand,
  ScalarFieldChangeDto
} from '../models/CustomFieldContent';
import { CustomFieldTab } from '../models/CustomFieldTab';

@Injectable({
  providedIn: 'root'
})
export class CustomFieldService {
  private clientRowSeed = 0;
  private readonly belgianNationalityNumberPattern = /^\d{2}\.?\d{2}\.?\d{2}-?\d{3}\.?\d{2}$/;

  constructor(
    private http: HttpClient,
    private _authService: AuthService,
    private labelService: LabelService
  ) { }

  // #region general
  public getStatusClass(status: GeneralStatus) {
    switch (status) {
      case GeneralStatus.InSetup: return '#EE7622';
      case GeneralStatus.Active: return '#28A745';
      case GeneralStatus.Inactive: return '#bfbfbf';
      case GeneralStatus.Deleted: return '#ff0000';
      default: return '#EE7622'
    }
  }

  public getStatusName(status: GeneralStatus) {
    switch (status) {
      case GeneralStatus.InSetup: return 'setup_in_progress';
      case GeneralStatus.Active: return 'active';
      case GeneralStatus.Inactive: return 'inactive';
      case GeneralStatus.Deleted: return 'deleted';
      case 3: return 'all';
      default: return 'setup_in_progress'
    }
  }

  public getFieldTypeLabel(fieldType: FieldType) {
    switch (fieldType) {
      case FieldType.AlphaNumericTextArea:
        return this.labelService.getLabel('field_type_textbox', 'custom_fields');
      case FieldType.AlphaNumericInput:
        return this.labelService.getLabel('field_type_text_field_one_line', 'custom_fields');
      case FieldType.Integer:
        return this.labelService.getLabel('field_type_integer', 'custom_fields');
      case FieldType.Decimal:
        return this.labelService.getLabel('field_type_decimal', 'custom_fields');
      case FieldType.CheckboxYesOrNo:
        return this.labelService.getLabel('field_type_checkbox_yes_or_no', 'custom_fields');
      case FieldType.Date:
        return this.labelService.getLabel('date', 'general');
      case FieldType.Time:
        return this.labelService.getLabel('time', 'conversations');
      case FieldType.List:
        return this.labelService.getLabel('field_type_dropdown_list', 'custom_fields');
      case FieldType.MultipleChoicePicklist:
        return this.labelService.getLabel('field_type_multiple_choice_list', 'custom_fields');
      case FieldType.Composite:
        return this.labelService.getLabel('field_type_composite', 'custom_fields');

      default: return this.labelService.getLabel('all', 'general');
    }
  }

  public getFieldValidationPattern(field: CustomField) {
    switch (field.fieldValidationType) {
      case FieldValidationType.BelgianNationalityNumber:
        return this.belgianNationalityNumberPattern.source;

      case FieldValidationType.BelgianIBAN:
        return '^[Bb][Ee](\\s?\\d){14}$';

      case FieldValidationType.BelgianMobilePhoneNumber:
        return '^(0|0032(\\(0\\))?|\\+32(\\(0\\))?)[\\s.\\-]*4([\\s.\\-]*\\d){8}$';

      case FieldValidationType.BelgianPhoneNumber:
        return '^(0|0032(\\(0\\))?|\\+32(\\(0\\))?)[\\s.\\-]*([1-9]\\d)([\\s.\\-]*\\d){6}$';

      case FieldValidationType.Email:
        return '^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$';

      default:
        return undefined;
    }
  }
  // #endregion general

  // #region API: Custom fields
  public getCustomFields(): Observable<CustomFieldTab[]> {
    return this.http.post<CustomFieldTab[]>('/api/customfields', null);
  }

  public createTab(tab: CustomFieldTab): Observable<number> {
    return this.http.post<number>('/api/customfields/create-tab', tab);
  }

  public createCategory(category: CustomFieldCategory): Observable<number> {
    return this.http.post<number>('/api/customfields/create-category', category);
  }

  public createField(field: CustomField): Observable<number> {
    return this.http.post<number>('/api/customfields/create-field', field);
  }

  public updateTab(tab: CustomFieldTab): Observable<void> {
    return this.http.post<void>('/api/customfields/update-tab/', tab);
  }

  public updateCategory(category: CustomFieldCategory): Observable<void> {
    return this.http.post<void>('/api/customfields/update-category/', category);
  }

  public updateField(field: CustomField): Observable<void> {
    return this.http.post<void>('/api/customfields/update-field/', field);
  }

  public changeCategoryStatus(categoryId: number, status: GeneralStatus): Observable<void> {
    return this.http.post<void>('/api/customfields/change-category-status/' + categoryId, status);
  }

  public changeFieldStatus(fieldId: number, status: GeneralStatus): Observable<void> {
    return this.http.post<void>('/api/customfields/change-field-status/' + fieldId, status);
  }

  public updateTabSortOrder(tabs: CustomFieldTab[]): Observable<void> {
    return this.http.post<void>('/api/customfields/update-tab-sortorder/', tabs);
  }

  public updateCategorySortOrder(categories: CustomFieldCategory[]): Observable<void> {
    return this.http.post<void>('/api/customfields/update-category-sortorder/', categories);
  }

  public updateFieldSortOrder(fields: CustomField[]): Observable<void> {
    return this.http.post<void>('/api/customfields/update-field-sortorder/', fields);
  }
  // #endregion API: Custom fields


  // #region Custom Fields Content
  public getCustomFieldApplicationFormContent(jobTemplateId: number, employeeId: number, candidateId: number | null, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/application-form/' + jobTemplateId, { role, employeeId, candidateId });
  }

  public saveCustomFieldApplicationFormContent(jobTemplateId: number, command: SaveCustomFieldContentCommand, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    const delegateStudentId = this._authService.getActiveDelegation()?.studentId ?? null;
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/application-form/' + jobTemplateId + '/save', { command, role, delegateStudentId }
    );
  }

  public getCustomFieldJobApplicationFormContent(jobId: number, employeeId: number, candidateId: number | null, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/job-application-form/' + jobId, { role, employeeId, candidateId });
  }

  public saveCustomFieldJobApplicationFormContent(jobId: number, command: SaveCustomFieldContentCommand, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    const delegateStudentId = this._authService.getActiveDelegation()?.studentId ?? null;
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/job-application-form/' + jobId + '/save', { command, role, delegateStudentId });
  }
  // #endregion Custom Fields Content


  // #region API: MWsfiches
  public getEmployeeTabs(role: GeneralActor = null): Observable<MenuItem[]> {
    let delegateStudentId = this._authService.getActiveDelegation()?.studentId ?? null;
    return this.http.post<MenuItem[]>('/api/customfields/employee-tabs', role !== null ? { role, delegateStudentId } : null);
  }
  // #endregion API: MWsfiches


  // #region Content DTO http requests
  public getCustomFieldTabContent(tabId: number, employeeId: number, candidateId: number, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/tab/' + tabId, { role, employeeId, candidateId });
  }

  public saveCustomFieldContentCommand(command: SaveCustomFieldContentCommand, role: GeneralActor): Observable<CustomFieldTabContentDto> {
    const delegateStudentId = this._authService.getActiveDelegation()?.studentId ?? null;
    return this.http.post<CustomFieldTabContentDto>('/api/customfields/content/save', { role, delegateStudentId, command });
  }

  public getScalarFieldLog(tabId: number, fieldId: number, employeeId: number, candidateId: number, role: GeneralActor): Observable<FieldLogDto> {
    return this.http.post<FieldLogDto>(`/api/customfields/content/tab/${tabId}/field/${fieldId}/log`, { role, employeeId, candidateId });
  }

  public getCompositeFieldLog(tabId: number, fieldId: number, employeeId: number, candidateId: number, role: GeneralActor): Observable<CompositeFieldLogDto> {
    return this.http.post<CompositeFieldLogDto>(`/api/customfields/content/tab/${tabId}/field/${fieldId}/composite-log`, { role, employeeId, candidateId });
  }
  // #endregion Content DTO http requests


  // #region DTO mapping
  public mapContentDtoToVm(dto: CustomFieldTabContentDto, picklists: Picklist[]): CustomFieldTabContentVm {
    return {
      subject: dto.subject,
      tab: {
        tabId: dto.tab.tabId,
        name: dto.tab.name,
        canEdit: dto.tab.canEdit
      },
      categories: dto.categories
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(category => this.mapCategoryDtoToVm(category, picklists))
    };
  }

  public buildSaveCommand(vm: CustomFieldTabContentVm): SaveCustomFieldContentCommand {
    const scalarChanges: ScalarFieldChangeDto[] = [];
    const compositeChanges: CompositeFieldChangeDto[] = [];

    for (const category of vm.categories) {
      for (const field of category.fields) {
        if (!field.canEdit) {
          continue;
        }

        if (field.fieldType !== FieldType.Composite && field.scalarValue) {
          const scalarChange = this.buildScalarChange(field);
          if (scalarChange) {
            scalarChanges.push(scalarChange);
          }
          continue;
        }

        if (field.fieldType === FieldType.Composite && field.composite) {
          const rowChanges = field.composite.rows
            .map(row => this.buildCompositeRowChange(field, row))
            .filter((row): row is CompositeRowChangeDto => !!row);

          if (rowChanges.length) {
            compositeChanges.push({
              fieldId: field.fieldId,
              rows: rowChanges
            });
          }
        }
      }
    }

    return {
      subject: vm.subject,
      tabId: vm.tab.tabId,
      scalarChanges,
      compositeChanges
    };
  }

  public createCompositeRow(field: CustomFieldVm): CompositeRowVm {
    return {
      rowId: null,
      clientRowId: this.nextClientRowId(),
      dateEffective: null,
      originalDateEffective: null,
      deleted: false,
      cells: (field.composite?.childFields ?? []).map(childField => ({
        fieldId: childField.fieldId,
        valueId: null,
        value: this.getDefaultVmValue(childField.fieldType),
        originalValue: undefined
      }))
    };
  }

  public buildApplicationFormSubmitCommand(vm: CustomFieldTabContentVm): SaveCustomFieldContentCommand {
    const command: SaveCustomFieldContentCommand = {
      subject: vm.subject,
      tabId: vm.tab.tabId,
      scalarChanges: [],
      compositeChanges: []
    };

    for (const category of vm.categories ?? []) {
      for (const field of category.fields ?? []) {
        if (!field.canEdit) {
          continue;
        }

        if (field.fieldType !== FieldType.Composite) {
          if (!field.scalarValue) {
            continue;
          }

          const serializedValue = this.formatContentValue(field.fieldType, field.scalarValue.value);
          const normalizedValue = this.normalizeComparableValue(field.fieldType, field.scalarValue.value);

          command.scalarChanges.push({
            fieldId: field.fieldId,
            valueId: field.scalarValue.valueId,
            operation: normalizedValue === null
              ? CustomFieldChangeOperation.Delete
              : CustomFieldChangeOperation.Upsert,
            value: serializedValue,
            dateEffective: field.scalarValue.dateEffective ?? null
          });

          continue;
        }

        if (!field.composite) {
          continue;
        }

        const rowChanges = [];

        for (const row of field.composite.rows ?? []) {
          if (row.deleted) {
            if (row.rowId) {
              rowChanges.push({
                rowId: row.rowId,
                clientRowId: row.clientRowId,
                operation: CustomFieldChangeOperation.Delete,
                dateEffective: row.dateEffective ?? null,
                cells: []
              });
            }

            continue;
          }

          const isEmpty = this.isCompositeRowEmpty(field, row);

          if (isEmpty) {
            if (row.rowId) {
              rowChanges.push({
                rowId: row.rowId,
                clientRowId: row.clientRowId,
                operation: CustomFieldChangeOperation.Delete,
                dateEffective: row.dateEffective ?? null,
                cells: []
              });
            }

            continue;
          }

          rowChanges.push({
            rowId: row.rowId,
            clientRowId: row.clientRowId,
            operation: CustomFieldChangeOperation.Upsert,
            dateEffective: row.dateEffective ?? null,
            cells: (field.composite.childFields ?? [])
              .filter(childField => childField.canEdit)
              .map(childField => {
                const cell = this.getCompositeCell(row, childField.fieldId);

                return {
                  fieldId: childField.fieldId,
                  valueId: cell.valueId,
                  value: this.formatContentValue(childField.fieldType, cell.value)
                };
              })
          });
        }

        if (rowChanges.length) {
          command.compositeChanges.push({
            fieldId: field.fieldId,
            rows: rowChanges
          });
        }
      }
    }

    return command;
  }

  public getCompositeCell(row: CompositeRowVm, fieldId: number): CompositeCellVm {
    let cell = row.cells.find(candidate => candidate.fieldId === fieldId);
    if (!cell) {
      cell = {
        fieldId,
        valueId: null,
        value: undefined,
        originalValue: undefined
      };
      row.cells.push(cell);
    }

    return cell;
  }

  public didScalarValueChange(field: CustomFieldVm): boolean {
    if (!field.scalarValue) {
      return false;
    }

    return !this.valuesEqualByField(field.fieldType, field.scalarValue.value, field.scalarValue.originalValue)
      || !this.datesEqual(field.scalarValue.dateEffective, field.scalarValue.originalDateEffective);
  }

  public didCompositeRowChange(field: CustomFieldVm, row: CompositeRowVm): boolean {
    if (row.deleted) {
      return !!row.rowId;
    }

    if (!this.datesEqual(row.dateEffective, row.originalDateEffective)) {
      return true;
    }

    return (field.composite?.childFields ?? []).some(childField => {
      const cell = this.getCompositeCell(row, childField.fieldId);
      return !this.valuesEqualByField(childField.fieldType, cell.value, cell.originalValue);
    });
  }

  public isCompositeRowEmpty(field: CustomFieldVm, row: CompositeRowVm): boolean {
    return !(field.composite?.childFields ?? []).some(childField => {
      const cell = this.getCompositeCell(row, childField.fieldId);
      return this.hasMeaningfulValue(this.formatContentValue(childField.fieldType, cell.value));
    });
  }

  public getContentFieldValidationPattern(field: Pick<CustomFieldVm, 'validationType'> | Pick<CompositeFieldVm['childFields'][number], 'validationType'>) {
    switch (field.validationType) {
      case FieldValidationType.BelgianNationalityNumber:
        return this.belgianNationalityNumberPattern.source;

      case FieldValidationType.BelgianIBAN:
        return '^[Bb][Ee](\\s?\\d){14}$';

      case FieldValidationType.BelgianMobilePhoneNumber:
        return '^(0|0032(\\(0\\))?|\\+32(\\(0\\))?)[\\s.\\-]*4([\\s.\\-]*\\d){8}$';

      case FieldValidationType.BelgianPhoneNumber:
        return '^(0|0032(\\(0\\))?|\\+32(\\(0\\))?)[\\s.\\-]*([1-9]\\d)([\\s.\\-]*\\d){6}$';

      case FieldValidationType.Email:
        return '^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$';

      default:
        return undefined;
    }
  }

  public isBelgianNationalityNumberField(field: Pick<CustomFieldVm, 'validationType'> | Pick<CompositeFieldVm['childFields'][number], 'validationType'>): boolean {
    return field.validationType === FieldValidationType.BelgianNationalityNumber;
  }

  public isValidBelgianNationalityNumber(value: string | number | null | undefined): boolean {
    if (value === undefined || value === null || value.toString().trim() === '') {
      return true;
    }

    const formattedValue = value.toString().trim();
    if (!this.belgianNationalityNumberPattern.test(formattedValue)) {
      return false;
    }

    const digits = formattedValue.replace(/\D/g, '');
    const year = Number(digits.substring(0, 2));
    const month = Number(digits.substring(2, 4));
    const day = Number(digits.substring(4, 6));
    const birthOrderNumber = Number(digits.substring(6, 9));
    const checksum = Number(digits.substring(9, 11));
    const firstNineDigits = Number(digits.substring(0, 9));

    if (birthOrderNumber < 1 || birthOrderNumber > 998) {
      return false;
    }

    return (this.isValidDate(1900 + year, month, day) && this.hasValidBelgianNationalityNumberChecksum(firstNineDigits, checksum))
      || (this.isValidDate(2000 + year, month, day) && this.hasValidBelgianNationalityNumberChecksum(2000000000 + firstNineDigits, checksum));
  }

  public formatContentValue(fieldType: FieldType, value: any): string | null {
    if (value === undefined || value === null) {
      return null;
    }

    switch (fieldType) {
      case FieldType.AlphaNumericInput:
      case FieldType.AlphaNumericTextArea:
        return value.toString().trim() === '' ? null : value.toString();

      case FieldType.Integer:
      case FieldType.Decimal:
      case FieldType.List:
        return value === '' ? null : value.toString();

      case FieldType.CheckboxYesOrNo:
        return value ? '1' : '0';

      case FieldType.Date:
        if (!(value instanceof Date) || isNaN(value.getTime())) {
          return null;
        }
        return value.getDate().toString().padStart(2, '0') + '/' +
          (value.getMonth() + 1).toString().padStart(2, '0') + '/' +
          value.getFullYear();

      case FieldType.Time:
        if (!value) {
          return null;
        }
        return `2000-01-01T${value.hour.toString().padStart(2, '0')}:${value.minute.toString().padStart(2, '0')}:${(value.second ?? 0).toString().padStart(2, '0')}`;

      case FieldType.MultipleChoicePicklist:
        return Array.isArray(value) && value.length ? value.join(',') : null;

      default:
        return value.toString();
    }
  }

  public normalizeComparableValue(fieldType: FieldType, value: any): string | null {
    const formatted = this.formatContentValue(fieldType, value);
    if (formatted === null || formatted === undefined || formatted.toString().trim() === '') {
      return null;
    }

    switch (fieldType) {
      case FieldType.MultipleChoicePicklist:
        return formatted
          .split(',')
          .map(candidate => +candidate)
          .filter(candidate => !isNaN(candidate))
          .sort((a, b) => a - b)
          .join(',');

      case FieldType.Date:
        return this.formatContentValue(fieldType, this.parseContentValue(fieldType, formatted));

      case FieldType.Time: {
        const parsed = this.parseContentValue(fieldType, formatted) as NgbTimeStruct;
        return this.formatContentValue(fieldType, parsed);
      }

      default:
        return formatted;
    }
  }
  // #endregion DTO mapping


  // #region PRIVATE HELPERS
  private valuesEqualByField(fieldType: FieldType, left: any, right: any): boolean {
    return this.normalizeComparableValue(fieldType, left) === this.normalizeComparableValue(fieldType, right);
  }

  private parseContentValue(fieldType: FieldType, value?: string | null): any {
    if (value === undefined || value === null || value === '') {
      return this.getDefaultVmValue(fieldType);
    }

    switch (fieldType) {
      case FieldType.Integer:
      case FieldType.Decimal:
      case FieldType.List:
        return +value;

      case FieldType.CheckboxYesOrNo:
        return value === '1';

      case FieldType.Date: {
        const parts = value.split('/').map(Number);
        if (parts.length >= 3) {
          return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        return null;
      }

      case FieldType.Time: {
        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) {
          return { hour: 0, minute: 0, second: 0 } as NgbTimeStruct;
        }

        return {
          hour: parsed.getHours(),
          minute: parsed.getMinutes(),
          second: parsed.getSeconds()
        } as NgbTimeStruct;
      }

      case FieldType.MultipleChoicePicklist:
        return value.split(',').map(Number).filter(candidate => !isNaN(candidate));

      default:
        return value;
    }
  }

  private datesEqual(left?: Date | string | null, right?: Date | string | null): boolean {
    const leftDate = left ? new Date(left) : null;
    const rightDate = right ? new Date(right) : null;

    if (!leftDate && !rightDate) {
      return true;
    }

    if (!leftDate || !rightDate) {
      return false;
    }

    return leftDate.toDateString() === rightDate.toDateString();
  }

  private mapCategoryDtoToVm(category: CustomFieldCategoryVm | any, picklists: Picklist[]): CustomFieldCategoryVm {
    return {
      categoryId: category.categoryId,
      name: category.name,
      sortOrder: category.sortOrder,
      fields: (category.fields as CustomFieldContentDto[])
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(field => this.mapFieldDtoToVm(field, picklists))
    };
  }

  private mapFieldDtoToVm(field: CustomFieldContentDto, picklists: Picklist[]): CustomFieldVm {
    const picklist = field.picklistId ? picklists.find(candidate => candidate.listTypeID == field.picklistId) : undefined;

    return {
      fieldId: field.fieldId,
      commonFieldId: field.commonFieldId,
      name: field.name,
      fieldType: field.fieldType,
      sortOrder: field.sortOrder,
      required: field.required,
      useDateEffective: field.useDateEffective,
      minimumAmount: field.minimumAmount,
      maximumAmount: field.maximumAmount,
      validationType: field.validationType,
      canView: field.canView,
      canEdit: field.canEdit,
      canViewLog: field.canViewLog,
      picklistId: field.picklistId,
      picklist,
      scalarValue: field.scalarValue ? {
        valueId: field.scalarValue.valueId,
        value: this.parseContentValue(field.fieldType, field.scalarValue.value ?? null),
        dateEffective: field.scalarValue.dateEffective ? new Date(field.scalarValue.dateEffective) : null,
        originalValue: this.parseContentValue(field.fieldType, field.scalarValue.value ?? null),
        originalDateEffective: field.scalarValue.dateEffective ? new Date(field.scalarValue.dateEffective) : null,
        status: field.scalarValue.status
      } : undefined,
      composite: field.composite ? {
        childFields: field.composite.childFields
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(childField => ({
            fieldId: childField.fieldId,
            name: childField.name,
            fieldType: childField.fieldType,
            sortOrder: childField.sortOrder,
            required: childField.required,
            minimumAmount: childField.minimumAmount,
            maximumAmount: childField.maximumAmount,
            validationType: childField.validationType,
            canView: childField.canView,
            canEdit: childField.canEdit,
            picklistId: childField.picklistId,
            picklist: childField.picklistId ? picklists.find(candidate => candidate.listTypeID == childField.picklistId) : undefined
          })),
        rows: field.composite.rows.map(row => ({
          rowId: row.rowId,
          clientRowId: row.clientRowId || this.nextClientRowId(),
          dateEffective: row.dateEffective ? new Date(row.dateEffective) : null,
          originalDateEffective: row.dateEffective ? new Date(row.dateEffective) : null,
          deleted: false,
          cells: field.composite!.childFields
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(childField => {
              const dtoCell = row.cells.find(cell => cell.fieldId === childField.fieldId);
              const parsedValue = this.parseContentValue(childField.fieldType, dtoCell?.value ?? null);
              return {
                fieldId: childField.fieldId,
                valueId: dtoCell?.valueId ?? null,
                value: parsedValue,
                originalValue: parsedValue
              };
            })
        }))
      } : undefined
    };
  }

  private buildScalarChange(field: CustomFieldVm): ScalarFieldChangeDto | null {
    if (!field.scalarValue || !this.didScalarValueChange(field)) {
      return null;
    }

    const normalizedValue = this.normalizeComparableValue(field.fieldType, field.scalarValue.value);
    if (normalizedValue === null) {
      return {
        fieldId: field.fieldId,
        valueId: field.scalarValue.valueId,
        operation: CustomFieldChangeOperation.Delete,
        value: null,
        dateEffective: field.scalarValue.dateEffective ?? null
      };
    }

    return {
      fieldId: field.fieldId,
      valueId: field.scalarValue.valueId,
      operation: CustomFieldChangeOperation.Upsert,
      value: this.formatContentValue(field.fieldType, field.scalarValue.value),
      dateEffective: field.scalarValue.dateEffective ?? null
    };
  }

  private buildCompositeRowChange(field: CustomFieldVm, row: CompositeRowVm): CompositeRowChangeDto | null {
    const isEmpty = this.isCompositeRowEmpty(field, row);
    const changed = this.didCompositeRowChange(field, row);

    if (row.deleted) {
      return row.rowId ? {
        rowId: row.rowId,
        clientRowId: row.clientRowId,
        operation: CustomFieldChangeOperation.Delete,
        dateEffective: row.dateEffective ?? null,
        cells: []
      } : null;
    }

    if (!row.rowId && isEmpty) {
      return null;
    }

    if (isEmpty && row.rowId) {
      return {
        rowId: row.rowId,
        clientRowId: row.clientRowId,
        operation: CustomFieldChangeOperation.Delete,
        dateEffective: row.dateEffective ?? null,
        cells: []
      };
    }

    if (!changed) {
      return null;
    }

    return {
      rowId: row.rowId,
      clientRowId: row.clientRowId,
      operation: CustomFieldChangeOperation.Upsert,
      dateEffective: row.dateEffective ?? null,
      cells: (field.composite?.childFields ?? []).map(childField => {
        const cell = this.getCompositeCell(row, childField.fieldId);
        return {
          fieldId: childField.fieldId,
          valueId: cell.valueId,
          value: this.formatContentValue(childField.fieldType, cell.value)
        };
      })
    };
  }

  private getDefaultVmValue(fieldType: FieldType): any {
    switch (fieldType) {
      case FieldType.CheckboxYesOrNo:
        return false;

      case FieldType.MultipleChoicePicklist:
        return [];

      default:
        return null;
    }
  }

  private hasMeaningfulValue(value?: string | null): boolean {
    return value !== undefined && value !== null && value.toString().trim() !== '';
  }

  private hasValidBelgianNationalityNumberChecksum(baseNumber: number, checksum: number): boolean {
    return 97 - (baseNumber % 97) === checksum;
  }

  private isValidDate(year: number, month: number, day: number): boolean {
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year
      && date.getMonth() === month - 1
      && date.getDate() === day;
  }

  private nextClientRowId(): string {
    this.clientRowSeed += 1;
    return `row_${Date.now()}_${this.clientRowSeed}`;
  }
  // #endregion PRIVATE HELPERS
}
